import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MarkdownComponent } from 'ngx-markdown';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { DateTimePipe, FileSizePipe } from '@nimmsta/ng-commons-lib/pipes';
import {
  FirmwareBundle,
  Software,
  SoftwareArtifact,
  SoftwareArtifactStatus,
  UserRole,
} from '@nimmsta/ng-commons-lib/models';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { SoftwareService } from '@nimmsta/ng-commons-lib/services/software';
import { SoftwareArtifactService } from '../../services/software/software-artifact.service';
import { TranslatePipe } from '@ngx-translate/core';
import { SoftwareArtifactDownloadButtonComponent } from '../software-artifact-download-button.component';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';

@Component({
  templateUrl: './software-artifact-single.component.html',
  styleUrls: [
    'software-artifact-single.component.scss',
    '../software-label.scss',
  ],
  imports: [
    TranslatePipe,
    SoftwareArtifactDownloadButtonComponent,
    MarkdownComponent,
    TranslatePipe,
    HeaderBarComponent,
    FileSizePipe,
    DateTimePipe,
    FormsModule,
    SelectModule,
  ],
})
export class SoftwareArtifactSingleComponent implements OnInit {
  readonly noHardwareVersionOptionValue = '__NO_HARDWARE_VERSION__';

  selectedHardwareVersion?: string;

  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);
  private softwareService = inject(SoftwareService);
  private softwareArtifactService = inject(SoftwareArtifactService);

  isAdmin = false;

  softwareId?: string;

  artifactId?: string;

  artifact?: SoftwareArtifact;

  software?: Software;

  SoftwareArtifactStatus = SoftwareArtifactStatus;

  ngOnInit(): void {
    this.authService.getCurrentUser().subscribe((user) => {
      this.isAdmin = user.role === UserRole.ADMIN;
    });
    this.activatedRoute.params.subscribe((params) => {
      this.softwareId = params['softwareId'];
      this.artifactId = params['artifactId'];
      this.softwareArtifactService.useSoftwareId(this.softwareId!);

      this.softwareArtifactService
        .get(this.artifactId!)
        .subscribe((artifact) => {
          this.artifact = artifact;
          this.selectedHardwareVersion = this.hardwareVersionOptions[0];
        });

      this.softwareService.get(this.softwareId!).subscribe((software) => {
        this.software = software;
      });
    });
  }

  get firmwareBundles(): FirmwareBundle[] {
    if (this.artifact?.firmwareBundles?.length) {
      return this.artifact.firmwareBundles;
    }
    if (this.artifact?.firmwareBundle) {
      return [this.artifact.firmwareBundle];
    }
    return [];
  }

  get hardwareVersionOptions(): string[] {
    return Array.from(
      new Set(
        this.firmwareBundles.map((bundle) =>
          this.getHardwareVersionOptionValue(bundle),
        ),
      ),
    );
  }

  get selectedFirmwareBundle(): FirmwareBundle | undefined {
    const firstOption = this.hardwareVersionOptions[0];
    const selectedHardwareVersion = this.selectedHardwareVersion ?? firstOption;
    return this.firmwareBundles.find(
      (bundle) =>
        this.getHardwareVersionOptionValue(bundle) === selectedHardwareVersion,
    );
  }

  onHardwareVersionChange(selectedHardwareVersion: string): void {
    this.selectedHardwareVersion = selectedHardwareVersion;
  }

  getHardwareVersionOptionValue(bundle: FirmwareBundle): string {
    return bundle.hardwareVersion ?? this.noHardwareVersionOptionValue;
  }
}
